import './elementor.scss';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { ConfigLoader } from '../../components/config/ConfigLoader';
import { ConfigProvider } from '../../components/config/ConfigProvider';
import { ToastContainer } from '../../components/ui/toast';
import { ToastProvider } from '../../contexts/ToastContext';
import { SummarizerConfig } from '../../types/summarizer';

// Production build - logging removed for performance

// Widget attributes interface
interface WidgetAttributes {
  toolType: string;
  configMode?: 'default' | 'override' | 'custom';
  configOverride?: Partial<SummarizerConfig>;
  customCSS?: string;
  enableCustomPrompt?: boolean;
  customPrompt?: string;
}

// Main widget component
function SummarizerWidget({
  toolType,
  configMode = 'default',
  configOverride,
  customCSS,
  enableCustomPrompt,
  customPrompt,
}: WidgetAttributes) {

  // Component mounted effect removed in production

  // Merge custom prompt from widget settings into config override
  const finalConfigOverride = React.useMemo(() => {
    let override = configOverride || {};

    // If custom prompt is enabled and provided, add it to the override
    if (enableCustomPrompt && customPrompt) {
      override = {
        ...override,
        prompt: customPrompt,
      };
    }

    return Object.keys(override).length > 0 ? override : undefined;
  }, [configOverride, enableCustomPrompt, customPrompt]);

  return (
    <ConfigProvider initialToolId={toolType}>
      <ConfigLoader
        toolType={toolType}
        configMode={configMode}
        configOverride={finalConfigOverride}
        customCSS={customCSS}
        className="tubeonai-elementor-widget"
      />
    </ConfigProvider>
  );
}

function initializeTubeOnAI() {
  // Check if already initialized to prevent duplicate runs
  if ((window as any).TubeOnAI?.initialized) {
    return;
  }

  if (!window.TubeOnAI) {
    (window as any).TubeOnAI = {};
  }
  
  // Mark as initialized
  (window as any).TubeOnAI.initialized = true;

  // Widget initialization function
  const checkAndInitWidgets = (): boolean => {
    let widgetsInitialized = false;
    
    const widgets = document.querySelectorAll(
      '.tubeonai-universal-widget-container[data-widget-attributes]'
    );
    
    widgets.forEach((widget) => {
      const id = widget.getAttribute('id');
      const hasReactRoot = widget.getAttribute('data-react-root');
      
      if (id && !hasReactRoot) {
        const attributesJson = widget.getAttribute('data-widget-attributes');
        if (attributesJson) {
          try {
            const attributes = JSON.parse(attributesJson) as WidgetAttributes;
            (window as any).TubeOnAI.initUniversalWidget(id, attributes);
            widgetsInitialized = true;
          } catch (error) {
            console.error('TubeOnAI: Error parsing widget attributes:', error);
          }
        }
      }
    });
    
    return widgetsInitialized;
  };

  // Check for widgets
  const initialWidgetsFound = checkAndInitWidgets();

  // Retry logic for dynamically added widgets (only if no widgets were found initially)
  let retryCount = 0;
  let retryInterval: NodeJS.Timeout | null = null;
  
  if (!initialWidgetsFound) {
    retryInterval = setInterval(() => {
      const widgetsFound = checkAndInitWidgets();
      
      if (widgetsFound) {
        // Stop retrying once we've found and initialized widgets
        if (retryInterval) {
          clearInterval(retryInterval);
        }
      } else {
        retryCount++;
        if (retryCount >= 10) {
          if (retryInterval) {
            clearInterval(retryInterval);
          }
        }
      }
    }, 500);
  }

  // Main initialization method
  (window as any).TubeOnAI.initUniversalWidget = (
    containerId: string,
    attributes: WidgetAttributes
  ) => {
    const container = document.getElementById(containerId);
    if (!container) {
      console.warn(`TubeOnAI: Container with ID "${containerId}" not found`);
      return;
    }

    try {
      // Check if React and ReactDOM are available
      if (!React) {
        throw new Error('React is not available');
      }
      if (!createRoot) {
        throw new Error('ReactDOM.createRoot is not available');
      }

      const root = createRoot(container);

      root.render(
        <ToastProvider>
          <div className="summarizer">
            <SummarizerWidget
              toolType={attributes.toolType}
              configMode={attributes.configMode}
              configOverride={attributes.configOverride}
              customCSS={attributes.customCSS}
              enableCustomPrompt={attributes.enableCustomPrompt}
              customPrompt={attributes.customPrompt}
            />
          </div>
          <ToastContainer />
        </ToastProvider>
      );

      container.setAttribute('data-react-root', 'true');
    } catch (error) {
      console.error('TubeOnAI: Failed to initialize widget:', error);
      container.innerHTML = `
        <div style="padding: 1rem; border: 1px solid #ef4444; border-radius: 0.5rem; background: #fef2f2; color: #dc2626;">
          <strong>Error:</strong> Failed to initialize TubeOnAI widget. ${error instanceof Error ? error.message : String(error)}
        </div>
      `;
    }
  };

  // Initialize existing widgets with data-widget-attributes
  const widgetsWithAttributes = document.querySelectorAll(
    '.tubeonai-universal-widget-container[data-widget-attributes]'
  );

  widgetsWithAttributes.forEach((widget) => {
    const id = widget.getAttribute('id');
    if (id && !widget.getAttribute('data-react-root')) {
      const attributesJson = widget.getAttribute('data-widget-attributes');
      if (attributesJson) {
        try {
          const attributes = JSON.parse(attributesJson) as WidgetAttributes;
          (window as any).TubeOnAI.initUniversalWidget(id, attributes);
        } catch (error) {
          // Silently ignore parsing errors in production
        }
      }
    }
  });

  // Check for pending widgets (from inline script fallback)
  if ((window as any).TubeOnAIPendingWidgets) {
    (window as any).TubeOnAIPendingWidgets.forEach((pending: any) => {
      (window as any).TubeOnAI.initUniversalWidget(pending.id, pending.attributes);
    });
    
    delete (window as any).TubeOnAIPendingWidgets;
  }
}

// Initialize immediately
try {
  initializeTubeOnAI();
} catch (error) {
  console.error('TubeOnAI: Error during immediate initialization:', error);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    try {
      initializeTubeOnAI();
    } catch (error) {
      console.error('TubeOnAI: Error during DOMContentLoaded initialization:', error);
    }
  });
}

// Export to prevent tree-shaking
export {};